import { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Flex, useTheme } from '@chakra-ui/react';
import { TimelineSelect } from 'components/TimelineSelect/TimelineSelect';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch, useSelector } from 'react-redux';
import { ArchivedGoalsTable } from 'screens/Team/Rabbit/Goals/Archived/ArchivedGoalsTable';
import { PermanentlyDeleteOKRConfirmationModal } from 'screens/Team/Rabbit/Goals/Archived/PermanentlyDeleteOKRConfirmationModal';
import { UnarchiveOKRConfirmationModal } from 'screens/Team/Rabbit/Goals/Archived/UnarchiveOKRConfirmationModal';
import { usePermanentlyDeleteOkrConfirmationModalLogic } from 'screens/Team/Rabbit/Goals/Archived/usePermanentlyDeleteOkrConfirmationModalLogic';
import { useUnarchiveOkrConfirmationModalLogic } from 'screens/Team/Rabbit/Goals/Archived/useUnarchiveOkrConfirmationModalLogic';
import { setIsArchivedView } from 'screens/Team/Rabbit/Goals/slice/GoalsSlice';
import { useTimeBoundTimePeriods } from 'screens/home/summary/components/Cards/Goals/hooks';
import { okrsPageSelector } from 'selectors/okrsPage';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { MODULE_KEYS_USER_PREFERENCE_PERSISTENCE } from 'constants/ProjectConstants';
import { modifyString, StringModifier } from 'constants/copyHelpers';
import { getPersistenceKey } from 'constants/helper';
import { sendPageViewEvent } from 'constants/mixpanelHelper';

import { AgGridSearch } from 'uiToolkitV1/AgGridWrapperV2';
import { PaperWithShadow } from 'uiToolkitV1/Paper';
import { TextHeader2 } from 'uiToolkitV1/Typography';

type Props = {
  renderBorderlessTable?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gridRef: any;
};

export const ArchivedGoalsIntentLandingPage = ({ renderBorderlessTable = false, gridRef }: Props) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const { data: timelineData } = useTimeBoundTimePeriods();

  const { selectedTimelines } = useSelector(okrsPageSelector);

  const { moduleName } = useOrganisationSettings();
  const [selectedTimeline, setSelectedTimeline] = useState(null);

  const gridStatePersistenceKey = useMemo(() => {
    // eslint-disable-next-line no-shadow
    const moduleName = MODULE_KEYS_USER_PREFERENCE_PERSISTENCE.GOAL;
    const viewId = null;
    const tabId = null;
    const uniqueId = 'ArchivedGoals';

    return getPersistenceKey({
      moduleName,
      viewId,
      tabId,
      uniqueId,
    });
  }, []);

  const {
    isConfirmationModalOpen,
    isUnarchiveObjectiveConfirmationModal,
    handleOpenConfirmationModal,
    handleCloseConfirmationModal,
    handleUnarchiveOkr,
  } = useUnarchiveOkrConfirmationModalLogic();

  const {
    isPermanentlyDeleteConfirmationModalOpen,
    isPermanentlyDeleteObjectiveConfirmationModal,
    handleOpenPermanentlyDeleteConfirmationModal,
    handleClosePermanentlyDeleteConfirmationModal,
    handlePermanentlyDeleteOkr,
  } = usePermanentlyDeleteOkrConfirmationModalLogic();

  useEffect(
    () => () => {
      dispatch(setIsArchivedView({ isArchivedView: false }));
    },
    [dispatch]
  );

  const handleSelect = useCallback((timeline) => {
    setSelectedTimeline(timeline);
  }, []);

  useEffect(() => {
    if (renderBorderlessTable) {
      setSelectedTimeline(selectedTimelines?.[0]);
    } else {
      setSelectedTimeline(timelineData?.current);
    }
  }, [timelineData, renderBorderlessTable, selectedTimelines]);

  useEffect(() => {
    sendPageViewEvent({
      eventProperties: { page: 'ArchivedGoals', type: 'None', source: 'None' },
    });
  }, []);

  return (
    <Box>
      {checkNewNavEnabled() || renderBorderlessTable ? null : (
        <Flex alignItems="center" as="h1" px={4} bgColor={theme.colors.surface.F5F7F9}>
          <TextHeader2 bold py={4} ml={!checkNewNavEnabled() ? 0 : 3} mr="8px">
            {`Archived ${modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE)}`} for
          </TextHeader2>
          <Box as="span" fontSize="20px" fontWeight={600}>
            <TimelineSelect selectedTimeline={selectedTimeline} onSelect={handleSelect} />
          </Box>
        </Flex>
      )}

      <PaperWithShadow
        m={checkNewNavEnabled() || renderBorderlessTable ? 0 : 4}
        p={renderBorderlessTable ? 0 : 3}
        borderRadius={renderBorderlessTable ? '0px' : '8px'}
        bgColor="white"
        boxShadow={renderBorderlessTable ? 'none' : theme.shadows.paper}
      >
        <Flex alignItems="center" justifyContent="space-between" pb={2}>
          <Box w="300px">
            <AgGridSearch gridRef={gridRef} placeholder="Search across type, archived by etc." cohortId={null} />
          </Box>
        </Flex>
        <ArchivedGoalsTable
          gridStatePersistenceKey={gridStatePersistenceKey}
          selectedTimeline={selectedTimeline}
          gridRef={gridRef}
          openUnarchiveConfirmationModal={handleOpenConfirmationModal}
          openPermanentlyDeleteConfirmationModal={handleOpenPermanentlyDeleteConfirmationModal}
        />
      </PaperWithShadow>
      <UnarchiveOKRConfirmationModal
        isObjective={isUnarchiveObjectiveConfirmationModal}
        handleUnarchiveOkr={handleUnarchiveOkr}
        isOpen={isConfirmationModalOpen}
        onClose={handleCloseConfirmationModal}
      />
      <PermanentlyDeleteOKRConfirmationModal
        isObjective={isPermanentlyDeleteObjectiveConfirmationModal}
        handleUnarchiveOkr={handlePermanentlyDeleteOkr}
        isOpen={isPermanentlyDeleteConfirmationModalOpen}
        onClose={handleClosePermanentlyDeleteConfirmationModal}
      />
    </Box>
  );
};
